import type {
  FileManagerPermission,
  ToolPermission,
} from '@/composeables/file-manager-capabilities';

// Capability tokens -> the exact named UI state a component needs. This layer stays inside the file
// manager (D6): it is this component's presentation concern, while the tier -> capability mapping it
// consumes is app authorization policy and lives in `@/composeables/file-manager-capabilities`.
//
// There is no `computeViewMode` any more. "May this user see this at all" stopped being a rendering
// decision when capabilities moved onto the mount: a mount the user cannot read is not in the table,
// and a host left with none renders its own notice instead of the pane (see canBrowse).
export type VisibilityMode = 'visible' | 'hidden';
export type DragMode = 'enabled' | 'disabled';

export const computeCreateFolderMode = (
  permissions: FileManagerPermission[],
): VisibilityMode =>
  permissions.includes('folders:upload') ? 'visible' : 'hidden';

export const computeUploadMode = (
  permissions: FileManagerPermission[],
): VisibilityMode =>
  permissions.includes('files:upload') ? 'visible' : 'hidden';

export const computeMoveMode = (
  permissions: FileManagerPermission[],
): DragMode => (permissions.includes('files:move') ? 'enabled' : 'disabled');

export const computeCopyMode = (
  permissions: FileManagerPermission[],
): DragMode => (permissions.includes('files:copy') ? 'enabled' : 'disabled');

/**
 * May this tier start an item drag at all — EITHER verb, because which one a drag ends in is not
 * knowable when it begins: it depends on which mount the drop lands in (transferPolicyFor). The drop
 * side then narrows it and applies the verb's own gate (useDropTarget's isPermitted).
 *
 * Deliberately not `computeMoveMode` alone. files:move and files:copy carry the same rule today, so
 * that reads as equivalent — but the whole reason rules.ts splits them is that they are free to
 * diverge, and the day they do, gating the source on move would leave a copy-only tier unable to
 * begin the drag its drop gate would have allowed.
 */
export const computeDragSourceMode = (
  permissions: FileManagerPermission[],
): DragMode =>
  computeMoveMode(permissions) === 'enabled' || computeCopyMode(permissions) === 'enabled'
    ? 'enabled'
    : 'disabled';

export const computePreviewMode = (
  permissions: FileManagerPermission[],
): DragMode => (permissions.includes('files:preview') ? 'enabled' : 'disabled');

export const computeDeleteMode = (
  permissions: FileManagerPermission[],
): DragMode =>
  permissions.includes('files:delete') ? 'enabled' : 'disabled';

export const computeRenameMode = (
  permissions: FileManagerPermission[],
): DragMode =>
  permissions.includes('files:rename') ? 'enabled' : 'disabled';

/**
 * ONE generic function for the whole tool registry, rather than one per tool — the deliberate
 * exception to the "every compute*Mode takes one argument and hardcodes its own token" convention
 * every function above follows.
 *
 * The convention exists so a component can never read the permissions array directly, and that
 * invariant is fully intact here: `modes.ts` is still the only interpreter of a permission string, and
 * `ToolPermission` narrows the parameter so nothing but a `tool:` token can be passed. What a
 * per-tool function would buy is a hardcoded token — which is precisely what the registry cannot have,
 * since its whole premise is that adding a tool is one new file plus one array entry. A
 * `computeOutlookMailCopyMode` per tool would make it three, one of them in this file.
 */
export const computeToolMode = (
  permissions: FileManagerPermission[],
  tool: ToolPermission,
): DragMode => (permissions.includes(tool) ? 'enabled' : 'disabled');

// Future precedent (not built now — files:edit doesn't exist yet, SharePoint button is still a dead
// stub): a 3-state slot plugs into the exact same convention, no new machinery required:
// export const computeSharePointButtonMode = (permissions: FileManagerPermission[]): 'edit' | 'view' | 'hidden' => {
//   if (permissions.includes('files:edit')) return 'edit';
//   if (permissions.includes('files:read')) return 'view';
//   return 'hidden';
// };
